import { computed, Signal, signal } from '@angular/core';

/**
 * Guards a user-triggered async action against being started again while it is still running
 * (e.g. double clicks on slow machines). Bind `busy()` to the `[disabled]` input of the triggering buttons.
 */
export interface BusyState {
  readonly busy: Signal<boolean>;
  /** Runs `fn` unless an action is already running; resolves to `undefined` if skipped. Always resets. */
  run<T>(fn: () => Promise<T>): Promise<T | undefined>;
}

export function createBusyState(): BusyState {
  const busy = signal(false);
  return {
    busy: busy.asReadonly(),
    async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
      if (busy()) return undefined;
      busy.set(true);
      try {
        return await fn();
      } finally {
        busy.set(false);
      }
    },
  };
}

/** Like {@link BusyState}, but tracks running actions per key (e.g. one per row). */
export interface KeyedBusyState<K> {
  readonly anyBusy: Signal<boolean>;
  isBusy(key: K): boolean;
  /** Runs `fn` unless an action for `key` is already running; resolves to `undefined` if skipped. */
  run<T>(key: K, fn: () => Promise<T>): Promise<T | undefined>;
}

export function createKeyedBusyState<K>(): KeyedBusyState<K> {
  const keys = signal<ReadonlySet<K>>(new Set());
  return {
    anyBusy: computed(() => keys().size > 0),
    isBusy: (key: K) => keys().has(key),
    async run<T>(key: K, fn: () => Promise<T>): Promise<T | undefined> {
      if (keys().has(key)) return undefined;
      keys.update(current => new Set(current).add(key));
      try {
        return await fn();
      } finally {
        keys.update(current => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
  };
}
